import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Sparkles } from "lucide-react";

interface Plan {
  id: string;
  name: string;
  description?: string | null;
  price_monthly?: number | null;
  price_yearly?: number | null;
  trial_days?: number | null;
  features?: any;
  is_popular?: boolean;
}

interface PublicPlanCardProps {
  plan: Plan;
  systemCode: string;
  onSelect: () => void;
}

export function PublicPlanCard({ plan, systemCode, onSelect }: PublicPlanCardProps) {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  // Build feature list from plan features
  const planFeatures = plan.features || {};
  const featureList: string[] = [];
  
  // Extract features from limits
  if (planFeatures.limits) {
    Object.entries(planFeatures.limits).forEach(([key, value]) => {
      if (value !== undefined && value !== null && typeof value === 'number') {
        // Convert key to readable label (e.g., fleet_users_max -> Usuários)
        const label = key.replace(/_max$/, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        featureList.push(`${label}: ${value === 999999 ? 'Ilimitado' : value}`);
      }
    });
  }

  // Extract features from toggles
  if (planFeatures.toggles) {
    Object.entries(planFeatures.toggles).forEach(([key, value]) => {
      if (value === true) {
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        featureList.push(label);
      }
    });
  }

  // If no features, add generic ones
  if (featureList.length === 0) {
    featureList.push('Acesso completo ao sistema');
    featureList.push('Suporte técnico incluído');
    if (plan.trial_days && plan.trial_days > 0) {
      featureList.push(`${plan.trial_days} dias de teste grátis`);
    }
  }

  const hasYearlyPrice = plan.price_yearly && plan.price_monthly;
  const yearlySavings = hasYearlyPrice 
    ? ((plan.price_monthly! * 12 - plan.price_yearly!) / (plan.price_monthly! * 12) * 100).toFixed(0)
    : 0;

  return (
    <Card className={`flex flex-col h-full transition-all duration-300 hover:shadow-xl ${plan.is_popular ? 'ring-2 ring-primary shadow-lg' : ''}`}>
      <CardHeader className="relative">
        {plan.is_popular && (
          <Badge className="absolute -top-3 right-4 bg-gradient-to-r from-[#A3FF3F] to-[#007CBE] text-black font-medium">
            <Sparkles className="w-3 h-3 mr-1" />
            Mais Popular
          </Badge>
        )}
        
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-foreground">{plan.name}</h3>
          {plan.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {plan.description}
            </p>
          )}
        </div>

        <div className="mt-4">
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold text-foreground">
              {plan.price_monthly ? formatPrice(plan.price_monthly) : 'Sob Consulta'}
            </span>
            {plan.price_monthly && <span className="text-muted-foreground">/mês</span>}
          </div>
          
          {hasYearlyPrice && (
            <div className="mt-2 space-y-1">
              <p className="text-sm text-muted-foreground">
                ou {formatPrice(plan.price_yearly!)} /ano
              </p>
              <Badge variant="secondary" className="text-xs bg-green-500/10 text-green-600 border-green-500/20">
                Economize {yearlySavings}% no plano anual
              </Badge>
            </div>
          )}

          {plan.trial_days && plan.trial_days > 0 && (
            <p className="mt-2 text-sm font-medium text-primary">
              {plan.trial_days} dias de teste grátis
            </p>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1">
        {featureList.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Recursos incluídos:</h4>
            <ul className="space-y-2">
              {featureList.slice(0, 6).map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            {featureList.length > 6 && (
              <p className="text-xs text-muted-foreground">
                + {featureList.length - 6} recursos adicionais
              </p>
            )}
          </div>
        )}
      </CardContent>

      <CardFooter>
        <Button 
          onClick={onSelect} 
          className="w-full"
          variant={plan.is_popular ? "default" : "outline"}
        >
          Assinar Agora
        </Button>
      </CardFooter>
    </Card>
  );
}
